import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Box, Button, TextField, Typography, Alert, MenuItem, Chip, Autocomplete } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { EmployeeFormData } from '../../types';
import { employeesAPI } from '../../services/api';
import { ROLES, EMPLOYMENT_TYPES, NAVIGATION, VALIDATION_MESSAGES } from '../../constants';
import { useTechnologyValues, useTechnologyLabels } from '../../hooks/useTechnologies';
import { useEmployee } from '../../hooks/useEmployees';
import { POSITIONS } from '../../constants';
import FileUpload from '../../components/files/FileUpload';
import { useFiles } from '../../hooks/useFiles';
import ProfilePictureUpload from '../../components/ProfilePictureUpload';
import EmployeeProjectHistory from '../../components/employees/EmployeeProjectHistory';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';

const schema = z.object({
  firstName: z.string().min(2, VALIDATION_MESSAGES.MIN_LENGTH(2)),
  lastName: z.string().min(2, VALIDATION_MESSAGES.MIN_LENGTH(2)),
  email: z.string().email().optional(),
  birthDate: z.string().min(1, VALIDATION_MESSAGES.REQUIRED),
  hireDate: z.string().min(1, VALIDATION_MESSAGES.REQUIRED),
  terminationDate: z.string().optional(),
  salary: z.string().optional(),
  bankAccount: z.string().optional(),
  address: z.string().optional(),
  phone: z.string().optional(),
  emergencyContact: z.string().optional(),
  role: z.enum([ROLES.ADMIN, ROLES.EMPLOYEE]),
  employmentType: z.enum([EMPLOYMENT_TYPES.CONTRACT, EMPLOYMENT_TYPES.FREELANCE]).optional(),
  position: z.string().optional(),
  technologies: z.array(z.string()).optional(),
});

const EditEmployee: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { user, updateUserProfilePicture } = useAuth();
  const { employee, isLoading: employeeLoading } = useEmployee(id!);
  const { files, uploadFile, deleteFile } = useFiles('EMPLOYEE', id!);
  const [profilePictureUrl, setProfilePictureUrl] = React.useState<string | null>(null);
  
  // Fetch technology data
  const { data: technologyValues = [], isLoading: technologyValuesLoading } = useTechnologyValues();
  const { data: technologyLabels = {}, isLoading: technologyLabelsLoading } = useTechnologyLabels();
  
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, watch, setValue } = useForm<EmployeeFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      birthDate: '',
      hireDate: '',
      terminationDate: '',
      salary: '',
      bankAccount: '',
      address: '',
      phone: '',
      emergencyContact: '',
      role: ROLES.EMPLOYEE,
      employmentType: EMPLOYMENT_TYPES.CONTRACT,
      position: POSITIONS[0] as string,
      technologies: [],
    }
  });
  const [success, setSuccess] = React.useState('');
  const [error, setError] = React.useState('');
  const [originalData, setOriginalData] = React.useState<EmployeeFormData | null>(null);
  
  const watchedValues = watch();
  const hasChanges = originalData && Object.keys(watchedValues).some(key => {
    const currentValue = watchedValues[key as keyof EmployeeFormData];
    const originalValue = originalData[key as keyof EmployeeFormData];
    
    // Handle special cases
    if (key === 'hireDate' && currentValue && originalValue) {
      return new Date(currentValue as string).toISOString().split('T')[0] !== new Date(originalValue as string).toISOString().split('T')[0];
    }
    if (key === 'salary') {
      const current = currentValue ? parseFloat(currentValue as string) : '';
      const original = originalValue ? parseFloat(originalValue as string) : '';
      return current !== original;
    }
    if (key === 'technologies') {
      const current = Array.isArray(currentValue) ? currentValue.sort() : [];
      const original = Array.isArray(originalValue) ? originalValue.sort() : [];
      return JSON.stringify(current) !== JSON.stringify(original);
    }
    
    return currentValue !== originalValue;
  });

  useEffect(() => {
    if (employee) {
      const formData = {
        firstName: employee.firstName || '',
        lastName: employee.lastName || '',
        email: employee.user?.email || '',
        birthDate: employee.birthDate ? employee.birthDate.substring(0, 10) : '',
        hireDate: employee.hireDate ? employee.hireDate.substring(0, 10) : '',
        terminationDate: employee.terminationDate ? employee.terminationDate.substring(0, 10) : '',
        salary: employee.salary ? employee.salary.toString() : '',
        bankAccount: employee.bankAccount || '',
        address: employee.address || '',
        phone: employee.phone || '',
        emergencyContact: employee.emergencyContact || '',
        role: employee.user?.role || ROLES.EMPLOYEE,
        employmentType: employee.employmentType || EMPLOYMENT_TYPES.CONTRACT,
        position: employee.position || POSITIONS[0] as string,
        technologies: employee.technologies || [],
      };
      
      // Set profile picture URL - prioritize user.profilePicture since that's what we update
      setProfilePictureUrl(employee.user?.profilePicture || employee.profilePicture || null);
      reset(formData);
      setOriginalData(formData);
    }
  }, [employee, reset]);

  const onSubmit = async (data: EmployeeFormData) => {
    setSuccess(''); setError('');
    try {
      const payload: any = {
        user: { role: data.role },
        firstName: data.firstName,
        lastName: data.lastName,
        birthDate: data.birthDate ? new Date(data.birthDate).toISOString() : undefined,
        hireDate: data.hireDate ? new Date(data.hireDate).toISOString() : undefined,
        terminationDate: data.terminationDate ? new Date(data.terminationDate).toISOString() : undefined,
      };
      if (data.salary) payload.salary = parseFloat(data.salary);
      if (data.bankAccount) payload.bankAccount = data.bankAccount;
      if (data.address) payload.address = data.address;
      if (data.phone) payload.phone = data.phone;
      if (data.emergencyContact) payload.emergencyContact = data.emergencyContact;
      if (data.employmentType) payload.employmentType = data.employmentType;
      if (data.position) payload.position = data.position;
      if (data.technologies) payload.technologies = data.technologies;
      await employeesAPI.update(id!, payload);
      setSuccess('Employee updated successfully!');
      navigate(NAVIGATION.EMPLOYEES);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update employee');
    }
  };

  if (employeeLoading || technologyValuesLoading || technologyLabelsLoading) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, px: { xs: 2, md: 0 } }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, px: { xs: 2, md: 0 } }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Edit Employee</Typography>
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Profile Picture Upload */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <ProfilePictureUpload
              currentImageUrl={profilePictureUrl}
              entityType="EMPLOYEE"
              entityId={id!}
              onImageChange={(imageUrl) => {
                setProfilePictureUrl(imageUrl);
                // If this is the current user being edited, update AuthContext as well
                if (employee && user && employee.user.id === user.id) {
                  updateUserProfilePicture(imageUrl);
                }
                // Invalidate the employees query to refresh employee data in other components
                queryClient.invalidateQueries({ queryKey: ['employees'] });
              }}
              disabled={isSubmitting}
            />
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="First Name" fullWidth margin="normal" {...register('firstName')} error={!!errors.firstName} helperText={errors.firstName?.message} InputLabelProps={{ shrink: true }} />
            <TextField label="Last Name" fullWidth margin="normal" {...register('lastName')} error={!!errors.lastName} helperText={errors.lastName?.message} InputLabelProps={{ shrink: true }} />
            <TextField label="Email" fullWidth margin="normal" {...register('email')} error={!!errors.email} helperText={errors.email?.message} InputLabelProps={{ shrink: true }} disabled />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="Birth Date" type="date" fullWidth margin="normal" {...register('birthDate')} InputLabelProps={{ shrink: true }} error={!!errors.birthDate} helperText={errors.birthDate?.message} />
            <TextField label="Hire Date" type="date" fullWidth margin="normal" {...register('hireDate')} InputLabelProps={{ shrink: true }} error={!!errors.hireDate} helperText={errors.hireDate?.message} />
            <TextField label="Termination Date" type="date" fullWidth margin="normal" {...register('terminationDate')} InputLabelProps={{ shrink: true }} error={!!errors.terminationDate} helperText={errors.terminationDate?.message} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="Salary" fullWidth margin="normal" {...register('salary')} error={!!errors.salary} helperText={errors.salary?.message} InputLabelProps={{ shrink: true }} />
            <TextField label="Bank Account" fullWidth margin="normal" {...register('bankAccount')} error={!!errors.bankAccount} helperText={errors.bankAccount?.message} InputLabelProps={{ shrink: true }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="Address" fullWidth margin="normal" {...register('address')} error={!!errors.address} helperText={errors.address?.message} InputLabelProps={{ shrink: true }} />
            <TextField label="Phone" fullWidth margin="normal" {...register('phone')} error={!!errors.phone} helperText={errors.phone?.message} InputLabelProps={{ shrink: true }} />
            <TextField label="Emergency Contact" fullWidth margin="normal" {...register('emergencyContact')} error={!!errors.emergencyContact} helperText={errors.emergencyContact?.message} InputLabelProps={{ shrink: true }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField
              label="Role"
              select
              fullWidth
              margin="normal"
              {...register('role')}
              error={!!errors.role}
              helperText={errors.role?.message}
              InputLabelProps={{ shrink: true }}
              value={watch('role') || ''}
            >
              <MenuItem value={ROLES.ADMIN}>Admin</MenuItem>
              <MenuItem value={ROLES.EMPLOYEE}>Employee</MenuItem>
            </TextField>
            <TextField
              label="Employment Type"
              select
              fullWidth
              margin="normal"
              {...register('employmentType')}
              error={!!errors.employmentType}
              helperText={errors.employmentType?.message}
              InputLabelProps={{ shrink: true }}
              value={watch('employmentType') || ''}
            >
              <MenuItem value={EMPLOYMENT_TYPES.CONTRACT}>Contract</MenuItem>
              <MenuItem value={EMPLOYMENT_TYPES.FREELANCE}>Freelance</MenuItem>
            </TextField>
            <TextField
              label="Position"
              select
              fullWidth
              margin="normal"
              {...register('position')}
              error={!!errors.position}
              helperText={errors.position?.message}
              InputLabelProps={{ shrink: true }}
              value={watch('position') || ''}
            >
              {POSITIONS.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Autocomplete
            multiple
            options={technologyValues}
            value={watch('technologies') || []}
            onChange={(_, newValue) => setValue('technologies', newValue)}
            getOptionLabel={(option) => technologyLabels[option] || option}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Technologies"
                margin="normal"
                error={!!errors.technologies}
                helperText={errors.technologies?.message}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...chipProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={technologyLabels[option] || option}
                    size="small"
                    {...chipProps}
                  />
                );
              })
            }
            filterSelectedOptions
            loading={technologyValuesLoading || technologyLabelsLoading}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" mb={2}>Files</Typography>
          <Box sx={{ width: '100%', overflow: 'auto' }}>
            <FileUpload
              entityType="EMPLOYEE"
              entityId={id!}
              files={files}
              uploadFile={uploadFile}
              deleteFile={deleteFile}
              onFilesChange={() => {}} // Files are managed by useFiles hook
            />
          </Box>
        </Box>

        {/* Project History */}
        {employee && <EmployeeProjectHistory employee={employee} />}

        <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }} disabled={isSubmitting || !hasChanges}>
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </form>
    </Box>
  );
};

export default EditEmployee; 